<template>
  <!-- A call in Workspace369: the floating call card the app shows over the inbox on desktop and
       tablets (lib/view/inbox/widgets/call/call_overlay.dart, call_actions.dart). Strings are the
       app's: the status line ("Incoming call", "On call · 02:31"), Answer / Decline, the call
       actions (Mute, Keypad, Hold, Record, Transfer, End call; Speaker only shows where the device supports it), the "Transfer call" panel
       (Transfer / Merge with their hints, Team, the App / Phone tags, "Waiting for …")
       and the call card's "Call ended" (thread/call_message_card.dart) and the call-ended banner (call_ended_banner.dart). The caller and teammates are sample data.
       Four screens share one fixed box and cross-fade on `state`:
       incoming → oncall → transfer → ended. With `hero`, the card rings, then answers once in a
       StoryScene; resting styles are the final frame (on call). -->
  <div class="clw" :class="[`clw--${state}`, { 'clw--hero': hero }]" aria-hidden="true">
    <div class="clw-thread">
      <header class="clw-head">
        <span><b>Maya Ortiz</b><small>+1 (512) 555-0182</small></span>
        <span class="clw-tag">Call</span>
      </header>
      <div class="clw-msgs">
        <p class="clw-msg">Hi! Can we move the shoot to Thursday the 29th?</p>
        <p class="clw-msg clw-msg--out">Sure, let me give you a quick call.</p>
      </div>
    </div>

    <div class="clw-card">
      <div class="clw-caller">
        <span class="clw-avatar">MO</span>
        <span class="clw-who"><b>Maya Ortiz</b><small>+1 (512) 555-0182</small></span>
      </div>

      <div class="clw-screens">
        <!-- Incoming -->
        <div class="clw-screen clw-incoming">
          <p class="clw-status clw-status--ring"><i />Incoming call</p>
          <div class="clw-answer">
            <span class="clw-round clw-round--decline"><svg viewBox="0 0 24 24"><path :d="icons.end" /></svg><small>Decline</small></span>
            <span class="clw-round clw-round--answer"><svg viewBox="0 0 24 24"><path :d="icons.phone" /></svg><small>Answer</small></span>
          </div>
        </div>

        <!-- On call -->
        <div class="clw-screen clw-oncall">
          <p class="clw-status"><i />On call · 02:31</p>
          <div class="clw-actions">
            <span v-for="a in actions" :key="a.label" class="clw-action" :class="{ 'is-on': a.on }">
              <svg viewBox="0 0 24 24"><path :d="icons[a.icon]" /></svg>{{ a.label }}
            </span>
          </div>
          <span class="clw-end"><svg viewBox="0 0 24 24"><path :d="icons.end" /></svg>End call</span>
        </div>

        <!-- Transfer -->
        <div class="clw-screen clw-transfer">
          <p class="clw-panel-head"><svg viewBox="0 0 24 24"><path :d="icons.back" /></svg>Transfer call</p>
          <div class="clw-modes">
            <span class="is-on">Transfer</span>
            <span>Merge</span>
          </div>
          <p class="clw-hint">Hand the caller over. You leave once your teammate answers.</p>
          <p class="clw-section">Team</p>
          <ul class="clw-team">
            <li class="is-on"><span class="clw-mini">SR</span>Sam Rivera<em>App</em></li>
            <li><span class="clw-mini">PS</span>Priya Shah<em>Phone</em></li>
          </ul>
          <p class="clw-waiting"><b>Waiting for Sam Rivera…</b><small>Your call is still active</small></p>
        </div>

        <!-- Ended -->
        <div class="clw-screen clw-ended">
          <p class="clw-ended-head"><svg viewBox="0 0 24 24"><path :d="icons.phone" /></svg><b>Call ended</b><small>6:41</small></p>
          <div class="clw-summary">
            <p class="clw-summary-head"><svg viewBox="0 0 24 24"><path :d="icons.sparkle" /></svg>Call summary</p>
            <p>Moving the shoot to Thursday, Oct 29 and adding a second set.</p>
          </div>
          <p class="clw-banner"><svg viewBox="0 0 24 24"><path :d="icons.end" /></svg>Call with Maya Ortiz ended · 6:41 · <em>View summary →</em></p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
defineProps({
  state: { type: String, default: "oncall" },
  hero: { type: Boolean, default: false },
});
const actions = [
  { label: "Mute", icon: "mic" },
  { label: "Keypad", icon: "dialpad" },
  { label: "Hold", icon: "pause" },
  { label: "Record", icon: "record", on: true },
  { label: "Transfer", icon: "forward" },
];
const icons = {
  phone: "M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z",
  end: "M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08a.956.956 0 0 1-.29-.7c0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28a11.27 11.27 0 0 0-2.67-1.85.996.996 0 0 1-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z",
  mic: "M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z",
  dialpad: "M12 19c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM6 1c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm12-8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm-6 8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm-6 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z",
  pause: "M6 19h4V5H6v14zm8-14v14h4V5h-4z",
  record: "M12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10z",
  forward: "M18 11l5-5-5-5v3h-4v4h4v3zm2 4.5c-1.25 0-2.45-.2-3.57-.57-.35-.11-.74-.03-1.02.24l-2.2 2.2c-2.83-1.44-5.15-3.75-6.59-6.59l2.2-2.21c.28-.26.36-.65.25-1C8.7 6.45 8.5 5.25 8.5 4c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1 0 9.39 7.61 17 17 17 .55 0 1-.45 1-1v-3.5c0-.55-.45-1-1-1z",
  back: "M15.41 7.41 14 6l-6 6 6 6 1.41-1.41L10.83 12z",
  sparkle: "M19 9l1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25L19 9zm-7.5.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12l-5.5-2.5zM19 15l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25L19 15z",
};
</script>

<style>
.clw {
  --clw-page: #fff;
  --clw-wash: #f4f6f8;
  --clw-line: rgb(0 0 0 / 0.1);
  --clw-text: #171717;
  --clw-muted: #5f6368;
  --clw-green: #166a3a;
  --clw-green-bg: #e3f4ea;
  --clw-red: #c62828;
  position: relative;
  height: 100%;
  overflow: hidden;
  border: 1px solid var(--clw-line);
  border-radius: 10px;
  background: var(--clw-wash);
  color: var(--clw-text);
  font-family: "Space Grotesk", Inter, ui-sans-serif, system-ui, sans-serif;
  font-size: 13px;
  line-height: 1.4;
  text-align: left;
  box-shadow: 0 18px 50px -18px rgb(15 23 42 / 0.28), 0 2px 6px rgb(15 23 42 / 0.06);
}
.dark .clw {
  --clw-page: #1c1c1c;
  --clw-wash: #242424;
  --clw-line: rgb(255 255 255 / 0.13);
  --clw-text: #ececec;
  --clw-muted: #a8a8a8;
  --clw-green: #6ed39a;
  --clw-green-bg: rgb(46 160 90 / 0.2);
  --clw-red: #ff8a80;
  box-shadow: 0 18px 50px -18px rgb(0 0 0 / 0.7);
}
.clw svg { width: 15px; height: 15px; flex: none; fill: currentColor; }
.clw-head { display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid var(--clw-line); background: var(--clw-page); padding: 10px 14px; }
.clw-head > span:first-child { display: grid; line-height: 1.25; }
.clw-head b { font-weight: 600; }
.clw-head small { color: var(--clw-muted); font-size: 11px; }
.clw-tag { border: 1px solid var(--clw-line); border-radius: 6px; padding: 2px 8px; font-size: 12px; }
.clw-msgs { display: grid; gap: 6px; padding: 12px 14px; }
.clw-msg { justify-self: start; max-width: 80%; margin: 0; border-radius: 10px; background: var(--clw-page); padding: 6px 10px; color: var(--clw-muted); font-size: 12.5px; }
.clw-msg--out { justify-self: end; }

/* The floating call card, over the conversation. */
.clw-card {
  position: absolute;
  right: 16px;
  bottom: 16px;
  left: 16px;
  display: grid;
  gap: 10px;
  max-width: 340px;
  margin-left: auto;
  border: 1px solid var(--clw-line);
  border-radius: 14px;
  background: var(--clw-page);
  padding: 14px;
  box-shadow: 0 22px 50px -16px rgb(15 23 42 / 0.4);
}
.clw-caller { display: flex; align-items: center; gap: 10px; }
.clw-avatar { display: grid; place-items: center; flex: none; width: 40px; height: 40px; border-radius: 10px; background: #e0e7ff; color: #3f58bf; font-size: 13px; font-weight: 700; }
.dark .clw-avatar { background: rgb(99 102 241 / 0.25); color: #c7d2fe; }
.clw-who { display: grid; line-height: 1.25; }
.clw-who b { font-size: 15px; font-weight: 600; }
.clw-who small { color: var(--clw-muted); font-size: 12px; }
.clw-screens { display: grid; }
.clw-screen { display: grid; align-content: space-between; gap: 10px; grid-area: 1 / 1; opacity: 0; visibility: hidden; transform: translateY(6px); transition: opacity 0.25s ease, transform 0.35s cubic-bezier(0.33, 1, 0.68, 1), visibility 0s linear 0.25s; }
.clw--incoming .clw-incoming,
.clw--oncall .clw-oncall,
.clw--transfer .clw-transfer,
.clw--ended .clw-ended { opacity: 1; visibility: visible; transform: none; transition: opacity 0.3s ease, transform 0.4s cubic-bezier(0.33, 1, 0.68, 1), visibility 0s; }

.clw-status { display: inline-flex; align-items: center; gap: 6px; justify-self: start; margin: 0; border-radius: 5px; background: var(--clw-green-bg); padding: 2px 8px; color: var(--clw-green); font-size: 12px; font-weight: 600; font-variant-numeric: tabular-nums; }
.clw-status i { width: 7px; height: 7px; border-radius: 2px; background: currentColor; }
.clw-ended-head { display: flex; align-items: center; gap: 7px; margin: 0; }
.clw-ended-head svg { color: var(--clw-green); }
.clw-ended-head b { font-weight: 600; }
.clw-ended-head small { margin-left: auto; color: var(--clw-muted); font-size: 12px; font-variant-numeric: tabular-nums; }
.clw-answer { display: flex; justify-content: space-around; padding: 4px 0; }
.clw-round { display: grid; justify-items: center; gap: 4px; font-size: 12px; }
.clw-round svg { box-sizing: content-box; width: 22px; height: 22px; border-radius: 12px; padding: 13px; color: #fff; }
.clw-round--answer svg { background: #1f8a4c; }
.clw-round--decline svg { background: #c62828; }
.clw-round small { color: var(--clw-muted); font-size: 12px; }
.clw-actions { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 5px; }
.clw-action { display: grid; justify-items: center; gap: 3px; border: 1px solid var(--clw-line); border-radius: 8px; padding: 7px 2px; font-size: 11px; }
.clw-action svg { width: 18px; height: 18px; }
.clw-action.is-on { border-color: rgb(198 40 40 / 0.35); background: rgb(198 40 40 / 0.08); color: var(--clw-red); }
.clw-end { display: flex; align-items: center; justify-content: center; gap: 6px; border-radius: 8px; background: #c62828; padding: 8px; color: #fff; font-weight: 600; }
.clw-panel-head { display: flex; align-items: center; gap: 4px; margin: 0; font-weight: 600; }
.clw-modes { display: grid; grid-template-columns: 1fr 1fr; border: 1px solid var(--clw-line); border-radius: 8px; padding: 2px; font-size: 12.5px; text-align: center; }
.clw-modes span { border-radius: 6px; padding: 4px; }
.clw-modes .is-on { background: var(--clw-text); color: var(--clw-page); font-weight: 600; }
.clw-hint { margin: 0; color: var(--clw-muted); font-size: 12px; }
/* The transfer panel is the tallest screen; it packs tighter so the card stays compact. */
.clw-transfer.clw-screen { align-content: start; gap: 7px; }
.clw-section { margin: 0; color: var(--clw-muted); font-size: 11.5px; font-weight: 600; }
.clw-team { display: grid; gap: 4px; margin: 0; padding: 0; list-style: none; }
.clw-team li { display: flex; align-items: center; gap: 8px; border-radius: 8px; padding: 4px 6px; }
.clw-team li.is-on { background: var(--clw-wash); }
.clw-team em { margin-left: auto; border: 1px solid var(--clw-line); border-radius: 5px; padding: 0 6px; color: var(--clw-muted); font-size: 11px; font-style: normal; }
.clw-mini { display: grid; place-items: center; width: 24px; height: 24px; border-radius: 7px; background: var(--clw-wash); font-size: 10px; font-weight: 700; }
.clw-team li.is-on .clw-mini { background: var(--clw-page); }
.clw-waiting { display: grid; margin: 0; border-left: 2px solid #1ba7ce; padding-left: 8px; }
.clw-waiting b { font-weight: 600; }
.clw-waiting small { color: var(--clw-muted); font-size: 11.5px; }
.clw-summary { display: grid; gap: 3px; border: 1px solid #ddd3f7; border-radius: 10px; background: #f3f0fd; padding: 8px 10px; font-size: 12.5px; }
.dark .clw-summary { border-color: rgb(160 110 220 / 0.35); background: rgb(121 72 175 / 0.18); }
.clw-summary p { margin: 0; }
.clw-summary-head { display: flex; align-items: center; gap: 5px; color: #5b3fb0; font-weight: 600; }
.dark .clw-summary-head { color: #d2b6f2; }
.clw-banner { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 6px; margin: 0; border-radius: 10px; background: #1f2937; padding: 8px 11px; color: #f8fafc; font-size: 12px; }
.clw-banner em { color: #67e8f9; font-style: normal; font-weight: 600; }
.dark .clw-banner { background: #f1f5f9; color: #0f172a; }
.dark .clw-banner em { color: #0e7490; }

/* Hero: the card rises ringing, then the call is answered. Resting styles are on call. */
.is-run .clw--hero .clw-card { animation: clw-rise 0.5s cubic-bezier(0.33, 1, 0.68, 1) 0.2s both; }
.is-run .clw--hero .clw-incoming { animation: clw-swap-out 0.3s ease 1.9s both; }
.is-run .clw--hero .clw-oncall { animation: clw-swap-in 0.4s cubic-bezier(0.33, 1, 0.68, 1) 2.1s both; }
.is-run .clw--hero .clw-round--answer svg { animation: clw-ring 0.9s ease-in-out 0.6s 2 both; }
@keyframes clw-rise { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: none; } }
@keyframes clw-swap-out { from { opacity: 1; visibility: visible; transform: none; } to { opacity: 0; visibility: hidden; transform: translateY(-6px); } }
@keyframes clw-swap-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
@keyframes clw-ring { 0%, 100% { transform: none; } 25% { transform: rotate(-12deg); } 75% { transform: rotate(12deg); } }
/* The thread stays readable above the card, so the call stage is taller than other scenes. The :not()
   lifts these above the stages' own `.sst:not(.sst--app)` heights, which otherwise win whenever their CSS loads later. */
.sst:not(.sst--app):has(.clw) { height: 600px; }
@media (max-width: 480px) {
  .sst:not(.sst--app):has(.clw) { height: 640px; }
  .clw-card { right: 10px; bottom: 10px; left: 10px; padding: 12px; }
  .clw-actions { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
@media (prefers-reduced-motion: reduce) {
  .clw-screen { transition: none; }
  .clw * { animation: none !important; }
}
</style>
